import { useState, useEffect } from 'react';
import {
  Receipt, Download, CheckCircle2, AlertCircle, Loader2, FileText, X, Clock, FileCheck,
} from 'lucide-react';
import { authenticatedFetch } from '@/lib/auth';

/**
 * Fakturaliste under /settings/ai-quotas (tab).
 *
 * Rolle-filtrert via GET /api/admin/ai-invoices:
 *   - systemadmin → alle fakturaer
 *   - admin → eget forbund
 *   - member/member-admin → egen org
 *
 * Handlinger:
 *   - Last ned PDF (blob-URL)
 *   - Marker som betalt + nullstill tokens (krever ≥20 tegn begrunnelse)
 */

interface Invoice {
  id: string;
  member_id: string;
  organization_name: string;
  contact_email: string | null;
  invoice_type: string;
  invoice_number: string;
  period_year: number;
  period_month: number;
  tokens_topup: number;
  total_amount_nok: number;
  due_date: string | null;
  status: string;
  sent_at: string | null;
  paid_at: string | null;
  created_at: string;
  has_pdf: boolean;
  overdue: boolean;
  marked_paid_by_name: string | null;
}

function fmtNok(n: number | string | null): string {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0,00 kr';
  return num.toLocaleString('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' kr';
}

function fmtDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('nb-NO', {
    day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Oslo',
  });
}

const MONTH_NB = ['januar', 'februar', 'mars', 'april', 'mai', 'juni',
  'juli', 'august', 'september', 'oktober', 'november', 'desember'];

function fmtPeriod(year: number, month: number): string {
  return `${MONTH_NB[month - 1]} ${year}`;
}

export default function AiInvoicesList() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'unpaid' | 'paid'>('all');
  const [downloading, setDownloading] = useState<string | null>(null);

  const [markPaidModal, setMarkPaidModal] = useState<Invoice | null>(null);
  const [reason, setReason] = useState('');
  const [markSaving, setMarkSaving] = useState(false);
  const [markError, setMarkError] = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const r = await authenticatedFetch('/api/admin/ai-invoices');
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${r.status}`);
      }
      const data = await r.json();
      setInvoices(data.invoices || []);
    } catch (err: any) {
      setError(err.message || 'Kunne ikke hente fakturaer');
    } finally {
      setLoading(false);
    }
  }

  async function downloadPdf(inv: Invoice) {
    if (!inv.has_pdf) {
      setError('PDF ikke tilgjengelig for denne fakturaen');
      return;
    }
    try {
      setDownloading(inv.id);
      const r = await authenticatedFetch(`/api/admin/ai-invoices/${inv.id}/pdf`);
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke laste ned PDF');
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${inv.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err: any) {
      setError(err.message || 'Nedlasting feilet');
    } finally {
      setDownloading(null);
    }
  }

  function openMarkPaid(inv: Invoice) {
    setMarkPaidModal(inv);
    setReason('');
    setMarkError('');
  }

  async function submitMarkPaid() {
    if (!markPaidModal) return;
    const trimmed = reason.trim();
    if (trimmed.length < 20) {
      setMarkError('Begrunnelse må være minst 20 tegn');
      return;
    }
    setMarkSaving(true);
    setMarkError('');
    try {
      const r = await authenticatedFetch(`/api/admin/ai-invoices/${markPaidModal.id}/mark-paid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: trimmed }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${r.status}`);
      }
      setMarkPaidModal(null);
      await load();
    } catch (err: any) {
      setMarkError(err.message || 'Kunne ikke markere som betalt');
    } finally {
      setMarkSaving(false);
    }
  }

  const filtered = invoices.filter(inv => {
    if (filterStatus === 'unpaid') return !inv.paid_at;
    if (filterStatus === 'paid') return !!inv.paid_at;
    return true;
  });

  const stats = {
    total: invoices.length,
    unpaid: invoices.filter(i => !i.paid_at).length,
    overdue: invoices.filter(i => i.overdue && !i.paid_at).length,
    paid: invoices.filter(i => !!i.paid_at).length,
  };

  if (loading) {
    return <div className="p-6 text-gray-600 flex items-center gap-2">
      <Loader2 className="h-4 w-4 animate-spin" /> Laster fakturaer…
    </div>;
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Filter-chips + sammendrag */}
      <div className="flex flex-wrap items-center gap-2">
        <FilterChip active={filterStatus === 'all'} onClick={() => setFilterStatus('all')}>
          Alle ({stats.total})
        </FilterChip>
        <FilterChip active={filterStatus === 'unpaid'} onClick={() => setFilterStatus('unpaid')}
          variant={stats.overdue > 0 ? 'warn' : 'default'}>
          Utestående ({stats.unpaid}){stats.overdue > 0 ? ` · ${stats.overdue} forfalt` : ''}
        </FilterChip>
        <FilterChip active={filterStatus === 'paid'} onClick={() => setFilterStatus('paid')}>
          Betalt ({stats.paid})
        </FilterChip>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-10 text-center text-gray-500">
          <Receipt className="h-10 w-10 mx-auto mb-3 text-gray-400" />
          <p className="font-medium">Ingen fakturaer å vise</p>
          <p className="text-sm mt-1">
            {filterStatus === 'unpaid' ? 'Alle fakturaer er betalt.' : 'Fakturaer utstedes automatisk når et medlem passerer 80 % av KI-kvoten.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fakturanr.</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Medlem</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Periode</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Beløp</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Forfall</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(inv => (
                <tr key={inv.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-mono text-gray-900">{inv.invoice_number}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{inv.organization_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{fmtPeriod(inv.period_year, inv.period_month)}</td>
                  <td className="px-4 py-3 text-sm text-right font-medium tabular-nums whitespace-nowrap">
                    {fmtNok(inv.total_amount_nok)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{fmtDate(inv.due_date)}</td>
                  <td className="px-4 py-3 text-xs">
                    {inv.paid_at ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">
                        <CheckCircle2 className="h-3 w-3" /> Betalt {fmtDate(inv.paid_at)}
                      </span>
                    ) : inv.overdue ? (
                      <span className="inline-flex items-center gap-1 text-red-700 bg-red-50 px-2 py-1 rounded-full">
                        <AlertCircle className="h-3 w-3" /> Forfalt
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-kf-orange-700 bg-kf-orange-50 px-2 py-1 rounded-full">
                        <Clock className="h-3 w-3" /> Utestående
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-2">
                      {inv.has_pdf && (
                        <button
                          onClick={() => downloadPdf(inv)}
                          disabled={downloading === inv.id}
                          className="text-sm text-kf-blue-600 hover:text-kf-blue-800 inline-flex items-center gap-1 disabled:opacity-50"
                        >
                          {downloading === inv.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                          PDF
                        </button>
                      )}
                      {!inv.paid_at && (
                        <button
                          onClick={() => openMarkPaid(inv)}
                          className="text-sm bg-emerald-50 text-emerald-700 hover:bg-emerald-100 px-3 py-1 rounded-md inline-flex items-center gap-1"
                          title="Marker som betalt + nullstill tokens for perioden"
                        >
                          <FileCheck className="h-4 w-4" />
                          Betalt
                        </button>
                      )}
                      {inv.marked_paid_by_name && (
                        <span className="text-xs text-gray-500" title={`Markert av ${inv.marked_paid_by_name}`}>
                          <FileText className="h-3 w-3 inline" />
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Marker-betalt modal */}
      {markPaidModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setMarkPaidModal(null)}>
          <div className="bg-white rounded-lg max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold">Marker som betalt</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {markPaidModal.invoice_number} · {markPaidModal.organization_name}
                </p>
              </div>
              <button onClick={() => setMarkPaidModal(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-400 rounded p-3 mb-4 text-sm text-blue-900">
              <p className="font-medium">Dette gjør to ting:</p>
              <ol className="list-decimal ml-5 mt-1 space-y-0.5">
                <li>Markerer fakturaen som betalt</li>
                <li>Nullstiller tokens-forbruket for <strong>{fmtPeriod(markPaidModal.period_year, markPaidModal.period_month)}</strong> — tjenesten åpner umiddelbart</li>
              </ol>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Begrunnelse (minst 20 tegn)
                </label>
                <textarea
                  rows={3}
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                  placeholder="F.eks.: Betaling mottatt på bankkonto 2026-11-05, referanse X — bekreftet av regnskap."
                />
                <p className="text-xs text-gray-500 mt-1">
                  {reason.trim().length}/20 tegn · lagres i audit-log
                </p>
              </div>

              {markError && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800">
                  <AlertCircle className="h-4 w-4 inline mr-1" /> {markError}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => setMarkPaidModal(null)}
                  disabled={markSaving}
                  className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md disabled:opacity-50"
                >
                  Avbryt
                </button>
                <button
                  onClick={submitMarkPaid}
                  disabled={markSaving || reason.trim().length < 20}
                  className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-md disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {markSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  {markSaving ? 'Lagrer…' : 'Bekreft betaling'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterChip({
  active, onClick, variant = 'default', children,
}: {
  active: boolean; onClick: () => void; variant?: 'default' | 'warn';
  children: React.ReactNode;
}) {
  const base = 'px-3 py-1.5 rounded-full text-sm font-medium transition-colors';
  const activeCls = variant === 'warn'
    ? 'bg-red-100 text-red-800 ring-2 ring-red-300'
    : 'bg-kf-blue-100 text-kf-blue-800 ring-2 ring-kf-blue-300';
  const inactiveCls = 'bg-gray-100 text-gray-700 hover:bg-gray-200';
  return (
    <button onClick={onClick} className={`${base} ${active ? activeCls : inactiveCls}`}>
      {children}
    </button>
  );
}
